import { useRef, useState } from 'react';
import { IMapCoordinates, IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import { useDispatch, useSelector } from 'react-redux';

import { setInfrastructureAction } from '../../../actions/infrastructure';
import { RECOMMENDED_NEWBUILDING_INFRASTRUCTURE_TYPE } from '../../../constants/infrastructure';
import { getIsDistanceCalculatingEnabled } from '../../../selectors/distanceCalculating';
import {
  getGeoCoordinates,
  getInfrastructureMapTypes,
  getMainPromoPin,
  getNewbuildingId,
  getPolygon,
} from '../../../selectors/newbuilding';
import {
  IRegularInfrastructureData,
  IRegularInfrastructureType,
  ITAInfrastructureData,
  TRegularInfrastructureItem,
  TRegularInfrastructureType,
  TTAInfrastructureType,
} from '../../../types/map';
import {
  getAndPrepareInfrastructureItems,
  getInfrastructureMapByYear,
  getRecommendationNewbuildings,
  getTransportAccessibilityInfrastructureMapByType,
} from '../helpers';
import { getMainGeoObject, getMainPolygonGeoObject } from '../helpers/dataSources';
import { toRegularGeoObjects } from '../helpers/mappers/toGeoObjects';

export const useGeoObjects = () => {
  const [regularInfrastructureTypes, setRegularInfrastructureTypes] = useState<IRegularInfrastructureType[] | null>(
    null,
  );

  const pinCoordinates = useSelector(getGeoCoordinates);
  const mainPromoPin = useSelector(getMainPromoPin);
  const newbuildingId = useSelector(getNewbuildingId);
  const newbuildingPolygon = useSelector(getPolygon);
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);
  const infrastructureMapTypes = useSelector(getInfrastructureMapTypes);
  const regularInfrastructureByYearRef = useRef(new Map<number, IRegularInfrastructureData>());
  const transportAccessibilityInfrastructureByTypeRef = useRef(new Map<TTAInfrastructureType, ITAInfrastructureData>());
  const hasMapInfrastructure = infrastructureMapTypes.length > 0;

  const dispatch = useDispatch();

  const setActualRegularInfrastructureTypes = (infrastructureItems: TRegularInfrastructureItem[]) => {
    const actualTypes: IRegularInfrastructureType[] = [
      ...infrastructureMapTypes,
      RECOMMENDED_NEWBUILDING_INFRASTRUCTURE_TYPE,
    ];

    const infrastructureItemTypes = new Set<TRegularInfrastructureType>(infrastructureItems.map(item => item.type));
    const existedTypes = actualTypes.filter(({ type }) => infrastructureItemTypes.has(type));

    setRegularInfrastructureTypes(existedTypes.length ? existedTypes : null);
  };

  const handleGeoObjectsFirstFetch = async (): Promise<IMapGeoObject[]> => {
    let geoObjectsResult: IMapGeoObject[] = [];

    const isFetched =
      regularInfrastructureByYearRef.current.size || transportAccessibilityInfrastructureByTypeRef.current.size;

    if (!isFetched) {
      const main = getMainGeoObject(pinCoordinates as IMapCoordinates, mainPromoPin);
      const mainPolygon = getMainPolygonGeoObject(newbuildingPolygon, isDistanceCalculatingEnabled);

      const [{ regularInfrastructure, transportAccessibilityInfrastructure }, recommendationNewBuildings] =
        await Promise.all([
          getAndPrepareInfrastructureItems(newbuildingId, hasMapInfrastructure),
          getRecommendationNewbuildings(newbuildingId),
        ]);

      const transportAccessibilityInfrastructureMapByType = getTransportAccessibilityInfrastructureMapByType(
        main,
        mainPolygon,
        transportAccessibilityInfrastructure,
      );
      transportAccessibilityInfrastructureByTypeRef.current = transportAccessibilityInfrastructureMapByType;

      const regularInfrastructureMapByYear = getInfrastructureMapByYear(
        regularInfrastructure,
        recommendationNewBuildings,
        main,
        mainPolygon,
      );
      regularInfrastructureByYearRef.current = regularInfrastructureMapByYear;

      const geoObjects = toRegularGeoObjects(regularInfrastructureMapByYear, new Date().getFullYear());
      geoObjectsResult = geoObjects;

      const regularInfrastructureItems = [...regularInfrastructure, ...recommendationNewBuildings];
      dispatch(setInfrastructureAction({ regularInfrastructureItems, transportAccessibilityInfrastructure }));
      setActualRegularInfrastructureTypes(regularInfrastructureItems);
    }

    return Promise.resolve(geoObjectsResult);
  };

  return {
    transportAccessibilityInfrastructureByTypeRef,
    regularInfrastructureByYearRef,
    regularInfrastructureTypes,
    handleGeoObjectsFirstFetch,
  };
};
